import type { CommunicationFacility } from "routes/Staff/MailRoom/Communications/CreateCommunications/types";

import AddLocationAltOutlined from "@mui/icons-material/AddLocationAltOutlined";
import Check from "@mui/icons-material/Check";
import Close from "@mui/icons-material/Close";
import { Button, Grid, IconButton, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { SparkleIcon } from "components/ArtificialIntelligence/SparkleIcon";
import { DateField } from "components/Forms/DateField";
import { FormSelect } from "components/Forms/FormSelect";
import { FormTextField } from "components/Forms/FormTextField";
import { TagPicker } from "components/TagPicker";
import {
  TagType,
  type DocumentType,
  type DocumentWithMetadataQuery,
} from "generated-graphql/graphql";
import { useCallback, useMemo, useState } from "react";
import { Controller, ControllerFieldState, type Control } from "react-hook-form";
import { FacilitiesPickerDialog } from "routes/Staff/MailRoom/Communications/CreateCommunications/components/FacilitiesPickerDialog";

import type { DocumentFacility, DocumentMetadataFormData, SuggestedFacility } from "../types";

import { DocumentFacilitiesGrid } from "./DocumentFacilitiesGrid";
import { RevertHelperLine } from "./RevertHelperLine";
import { SuggestedFacilitiesGrid } from "./SuggestedFacilitiesGrid";

type DocumentWithMetadataResult = DocumentWithMetadataQuery["document"];

type DocumentMetadataFormSectionsProps = {
  control: Control<DocumentMetadataFormData>;
  document?: DocumentWithMetadataResult;
  suggestedFacilities: SuggestedFacility[];
  suggestedFacilitiesLoading?: boolean;
  documentTypeOptions: { value: DocumentType; display: string }[];
  tenantId: string;
};

export function DocumentMetadataFormSections({
  control,
  document,
  suggestedFacilities,
  suggestedFacilitiesLoading,
  documentTypeOptions,
  tenantId,
}: DocumentMetadataFormSectionsProps) {
  const [isFacilitiesDialogOpen, setFacilitiesDialogOpen] = useState(false);
  const [rejectedFacilityIds, setRejectedFacilityIds] = useState<Set<string>>(new Set());
  const theme = useTheme();

  const needsReview = useMemo(() => document?.documentMetadata?.needsReview === true, [document]);
  const showSuggestedFacilities = needsReview;

  const isSuggestedValue = useCallback(
    (value: string | number | null, fieldState: ControllerFieldState) => {
      if (value === null) {
        return false;
      }
      return value !== "" && !fieldState.isDirty && needsReview;
    },
    [needsReview]
  );
  return (
    <>
      <Grid size={{ xs: 12 }}>
        <Typography variant="body1" color="text.secondary" mb={theme.spacing(3)}>
          We took a look at this document and filled out what we could. Please double check our work
          and update any details before marking as reviewed. This information can always be edited
          later.
        </Typography>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Controller
          name="documentTitle"
          control={control}
          render={({ field, fieldState }) => (
            <FormTextField
              {...field}
              label="Document Title"
              fullWidth
              textFieldProps={{ multiline: true, minRows: 1, maxRows: 4 }}
              suggestionProps={{
                showFullSuggestionChip: true,
                tooltip:
                  "Suggested title uses a naming convention that will be standard and searchable in Document Library",
                isSuggested: isSuggestedValue(field.value, fieldState),
                onRejectSuggestion: () => {
                  field.onChange(document?.originalTitle ?? "");
                },
              }}
              helperText={
                document?.originalTitle && document.originalTitle !== field.value ? (
                  <RevertHelperLine
                    label="Original title"
                    displayValue={document.originalTitle}
                    tooltip="Click to restore the original document title"
                    onClick={() => {
                      field.onChange(document.originalTitle);
                    }}
                  />
                ) : undefined
              }
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Controller
          name="facilities"
          control={control}
          render={({ field, fieldState }) => {
            const selectedFacilities = (field.value ?? []) as DocumentFacility[];

            const selectedFacilityIds = new Set(selectedFacilities.map((f) => f.id));

            const visibleSuggestedFacilities = showSuggestedFacilities
              ? suggestedFacilities.filter(
                  (facility) =>
                    !rejectedFacilityIds.has(facility.id) && !selectedFacilityIds.has(facility.id)
                )
              : [];

            const handleDialogSave = (facilities: CommunicationFacility[]) => {
              const mappedFacilities: DocumentFacility[] = facilities.map((facility) => ({
                id: facility.id,
                name: facility.name ?? "",
                customerFacilityId: facility.customerFacilityId ?? null,
                streetAddress1: facility.streetAddress1 ?? null,
                streetAddress2: null,
                city: facility.city ?? null,
                state: facility.state ?? null,
                zip: facility.zip ?? null,
              }));
              field.onChange(mappedFacilities);
              setFacilitiesDialogOpen(false);
            };

            const handleAddFacility = (facility: SuggestedFacility) => {
              if ((selectedFacilities ?? []).some((existing) => existing.id === facility.id)) {
                return;
              }
              field.onChange([...(selectedFacilities ?? []), facility]);
              // Remove from rejected list if it was there
              setRejectedFacilityIds((prev) => {
                const next = new Set(prev);
                next.delete(facility.id);
                return next;
              });
            };

            const handleRejectFacility = (facilityId: string) => {
              setRejectedFacilityIds((prev) => new Set(prev).add(facilityId));
            };

            const handleAcceptAll = () => {
              const facilitiesToAdd = visibleSuggestedFacilities.filter(
                (facility) =>
                  !(selectedFacilities ?? []).some((existing) => existing.id === facility.id)
              );
              field.onChange([...(selectedFacilities ?? []), ...facilitiesToAdd]);
              setRejectedFacilityIds(new Set());
            };

            const handleClearAll = () => {
              setRejectedFacilityIds((prev) => {
                const next = new Set(prev);
                visibleSuggestedFacilities.forEach((facility) => {
                  next.add(facility.id);
                });
                return next;
              });
            };

            return (
              <>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 1 }}
                >
                  <Typography variant="subtitle2">Associated Facilities</Typography>
                  <Tooltip title="Manage facilities">
                    <span>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => setFacilitiesDialogOpen(true)}
                        disabled={!tenantId}
                      >
                        <AddLocationAltOutlined fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
                <DocumentFacilitiesGrid
                  facilities={selectedFacilities}
                  loading={Boolean(suggestedFacilitiesLoading)}
                  noRowsButtonProps={{
                    buttonText: "Add facility",
                    onClick: () => setFacilitiesDialogOpen(true),
                  }}
                  onRemoveFacility={(facilityId) => {
                    field.onChange(
                      (selectedFacilities ?? []).filter((facility) => {
                        return facility.id !== facilityId;
                      })
                    );
                  }}
                />
                {fieldState.error && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                    {fieldState.error.message}
                  </Typography>
                )}

                {showSuggestedFacilities && visibleSuggestedFacilities.length > 0 && (
                  <>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ mb: 1, mt: 3 }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <SparkleIcon opacity={0.5} sx={{ fontSize: 16 }} />
                        <Typography variant="caption" color="text.secondary">
                          Suggested Facilities
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          startIcon={<Check />}
                          onClick={handleAcceptAll}
                          color="primary"
                        >
                          Accept All
                        </Button>
                        <Button
                          size="small"
                          startIcon={<Close />}
                          onClick={handleClearAll}
                          sx={{ color: theme.palette.text.secondary }}
                        >
                          Clear All
                        </Button>
                      </Stack>
                    </Stack>
                    <SuggestedFacilitiesGrid
                      facilities={visibleSuggestedFacilities}
                      loading={Boolean(suggestedFacilitiesLoading)}
                      onAddFacility={handleAddFacility}
                      onRejectFacility={handleRejectFacility}
                    />
                  </>
                )}

                {isFacilitiesDialogOpen && (
                  <FacilitiesPickerDialog
                    tenantId={tenantId}
                    initialFacilityIds={selectedFacilities?.map((facility) => facility.id)}
                    close={() => setFacilitiesDialogOpen(false)}
                    saveFacilities={handleDialogSave}
                  />
                )}
              </>
            );
          }}
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Typography
          variant="subtitle2"
          color="text.primary"
          sx={{ mb: theme.spacing(3), mt: theme.spacing(2) }}
        >
          Document Classification
        </Typography>
      </Grid>

      <Grid size={{ xs: 12, sm: 8 }}>
        <Controller
          name="documentType"
          control={control}
          rules={{ required: "Document type is required" }}
          render={({ field, fieldState }) => (
            <FormSelect
              name={field.name}
              control={control}
              label="Document Type"
              selectItems={documentTypeOptions}
              suggestionProps={{ isSuggested: isSuggestedValue(field.value, fieldState) }}
              helperText={
                document?.originalDocumentType && document.originalDocumentType !== field.value ? (
                  <RevertHelperLine
                    label="Original type"
                    displayValue={
                      documentTypeOptions.find(
                        (option) => option.value === document?.originalDocumentType
                      )?.display ?? document?.originalDocumentType
                    }
                    tooltip="Click to restore the original document type"
                    onClick={() => {
                      field.onChange(document?.originalDocumentType);
                    }}
                  />
                ) : undefined
              }
            />
          )}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 4 }}>
        <Controller
          name="programArea"
          control={control}
          render={({ field, fieldState }) => (
            <FormTextField
              {...field}
              label="Program Area"
              fullWidth
              suggestionProps={{ isSuggested: isSuggestedValue(field.value, fieldState) }}
            />
          )}
        />
      </Grid>

      <Grid size={{ xs: 4 }}>
        <Controller
          name="dateAuthored"
          control={control}
          render={({ field, fieldState }) => (
            <DateField
              {...field}
              label="Date Authored"
              suggestionProps={{ isSuggested: isSuggestedValue(field.value, fieldState) }}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 8 }}>
        <Controller
          name="documentTags"
          control={control}
          render={({ field, fieldState }) => (
            <TagPicker {...field} {...fieldState} tagType={TagType.Document} label="Tags" />
          )}
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Controller
          name="description"
          control={control}
          render={({ field, fieldState }) => (
            <FormTextField
              {...field}
              label="Description"
              fullWidth
              textFieldProps={{ multiline: true, minRows: 1, maxRows: 6 }}
              InputProps={{ sx: { alignItems: "start" } }}
              suggestionProps={{ isSuggested: isSuggestedValue(field.value, fieldState) }}
            />
          )}
        />
      </Grid>

      <Grid size={{ xs: 6 }}>
        <Controller
          name="documentSubtype"
          control={control}
          render={({ field, fieldState }) => (
            <FormTextField
              {...field}
              label="Document Subtype"
              fullWidth
              suggestionProps={{ isSuggested: isSuggestedValue(field.value, fieldState) }}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 6 }}>
        <Controller
          name="documentNumber"
          control={control}
          render={({ field, fieldState }) => (
            <FormTextField
              {...field}
              label="Document Number"
              fullWidth
              suggestionProps={{ isSuggested: isSuggestedValue(field.value, fieldState) }}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 6 }}>
        <Controller
          name="revisionNumber"
          control={control}
          render={({ field, fieldState }) => (
            <FormTextField
              {...field}
              label="Revision Number"
              fullWidth
              suggestionProps={{ isSuggested: isSuggestedValue(field.value, fieldState) }}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 6 }}>
        <Controller
          name="relatedDocumentNumber"
          control={control}
          render={({ field, fieldState }) => (
            <FormTextField
              {...field}
              label="Related Document Number"
              fullWidth
              suggestionProps={{ isSuggested: isSuggestedValue(field.value, fieldState) }}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 6 }}>
        <Controller
          name="reportingPeriod"
          control={control}
          render={({ field, fieldState }) => (
            <FormTextField
              {...field}
              label="Reporting Period"
              fullWidth
              suggestionProps={{ isSuggested: isSuggestedValue(field.value, fieldState) }}
            />
          )}
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Typography
          variant="subtitle2"
          color="text.primary"
          sx={{ mb: theme.spacing(3), mt: theme.spacing(2) }}
        >
          Site/Facility Information
        </Typography>
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Controller
          name="siteName"
          control={control}
          render={({ field, fieldState }) => (
            <FormTextField
              {...field}
              label="Site Name"
              fullWidth
              suggestionProps={{ isSuggested: isSuggestedValue(field.value, fieldState) }}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Controller
          name="siteAddressLine1"
          control={control}
          render={({ field, fieldState }) => (
            <FormTextField
              {...field}
              label="Site Address Line 1"
              fullWidth
              suggestionProps={{ isSuggested: isSuggestedValue(field.value, fieldState) }}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Controller
          name="siteAddressLine2"
          control={control}
          render={({ field, fieldState }) => (
            <FormTextField
              {...field}
              label="Site Address Line 2"
              fullWidth
              suggestionProps={{ isSuggested: isSuggestedValue(field.value, fieldState) }}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 4 }}>
        <Controller
          name="siteCity"
          control={control}
          render={({ field, fieldState }) => (
            <FormTextField
              {...field}
              label="Site City"
              fullWidth
              suggestionProps={{ isSuggested: isSuggestedValue(field.value, fieldState) }}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 4 }}>
        <Controller
          name="siteState"
          control={control}
          render={({ field, fieldState }) => (
            <FormTextField
              {...field}
              label="Site State"
              fullWidth
              suggestionProps={{ isSuggested: isSuggestedValue(field.value, fieldState) }}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 4 }}>
        <Controller
          name="siteZipCode"
          control={control}
          render={({ field, fieldState }) => (
            <FormTextField
              {...field}
              label="Site Zip Code"
              fullWidth
              suggestionProps={{ isSuggested: isSuggestedValue(field.value, fieldState) }}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Controller
          name="siteEmail"
          control={control}
          render={({ field, fieldState }) => (
            <FormTextField
              {...field}
              label="Site Email"
              fullWidth
              suggestionProps={{ isSuggested: isSuggestedValue(field.value, fieldState) }}
            />
          )}
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Typography
          variant="subtitle2"
          color="text.primary"
          sx={{ mb: theme.spacing(3), mt: theme.spacing(2) }}
        >
          Mailing Address
        </Typography>
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Controller
          name="mailingName"
          control={control}
          render={({ field, fieldState }) => (
            <FormTextField
              {...field}
              label="Mailing Name"
              fullWidth
              suggestionProps={{ isSuggested: isSuggestedValue(field.value, fieldState) }}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Controller
          name="mailingAddressLine1"
          control={control}
          render={({ field, fieldState }) => (
            <FormTextField
              {...field}
              label="Mailing Address Line 1"
              fullWidth
              suggestionProps={{ isSuggested: isSuggestedValue(field.value, fieldState) }}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Controller
          name="mailingAddressLine2"
          control={control}
          render={({ field, fieldState }) => (
            <FormTextField
              {...field}
              label="Mailing Address Line 2"
              fullWidth
              suggestionProps={{ isSuggested: isSuggestedValue(field.value, fieldState) }}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 4 }}>
        <Controller
          name="mailingCity"
          control={control}
          render={({ field, fieldState }) => (
            <FormTextField
              {...field}
              label="Mailing City"
              fullWidth
              suggestionProps={{ isSuggested: isSuggestedValue(field.value, fieldState) }}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 4 }}>
        <Controller
          name="mailingState"
          control={control}
          render={({ field, fieldState }) => (
            <FormTextField
              {...field}
              label="Mailing State"
              fullWidth
              suggestionProps={{ isSuggested: isSuggestedValue(field.value, fieldState) }}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 4 }}>
        <Controller
          name="mailingZipCode"
          control={control}
          render={({ field, fieldState }) => (
            <FormTextField
              {...field}
              label="Mailing Zip Code"
              fullWidth
              suggestionProps={{ isSuggested: isSuggestedValue(field.value, fieldState) }}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Controller
          name="mailingEmail"
          control={control}
          render={({ field, fieldState }) => (
            <FormTextField
              {...field}
              label="Mailing Email"
              fullWidth
              suggestionProps={{ isSuggested: isSuggestedValue(field.value, fieldState) }}
            />
          )}
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Typography
          variant="subtitle2"
          color="text.primary"
          sx={{ mb: theme.spacing(3), mt: theme.spacing(2) }}
        >
          Contact Information
        </Typography>
      </Grid>
      <Grid size={{ xs: 6 }}>
        <Controller
          name="contactName"
          control={control}
          render={({ field, fieldState }) => (
            <FormTextField
              {...field}
              label="Contact Name"
              fullWidth
              suggestionProps={{ isSuggested: isSuggestedValue(field.value, fieldState) }}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 6 }}>
        <Controller
          name="contactEmail"
          control={control}
          render={({ field, fieldState }) => (
            <FormTextField
              {...field}
              label="Contact Email"
              fullWidth
              suggestionProps={{ isSuggested: isSuggestedValue(field.value, fieldState) }}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 6 }}>
        <Controller
          name="contactPhone"
          control={control}
          render={({ field, fieldState }) => (
            <FormTextField
              {...field}
              label="Contact Phone"
              fullWidth
              suggestionProps={{ isSuggested: isSuggestedValue(field.value, fieldState) }}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 6 }}>
        <Controller
          name="agency"
          control={control}
          render={({ field, fieldState }) => (
            <FormTextField
              {...field}
              label="Agency"
              fullWidth
              suggestionProps={{ isSuggested: isSuggestedValue(field.value, fieldState) }}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Controller
          name="contactAddressLine1"
          control={control}
          render={({ field, fieldState }) => (
            <FormTextField
              {...field}
              label="Contact Address Line 1"
              fullWidth
              suggestionProps={{ isSuggested: isSuggestedValue(field.value, fieldState) }}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Controller
          name="contactAddressLine2"
          control={control}
          render={({ field, fieldState }) => (
            <FormTextField
              {...field}
              label="Contact Address Line 2"
              fullWidth
              suggestionProps={{ isSuggested: isSuggestedValue(field.value, fieldState) }}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 4 }}>
        <Controller
          name="contactCity"
          control={control}
          render={({ field, fieldState }) => (
            <FormTextField
              {...field}
              label="Contact City"
              fullWidth
              suggestionProps={{ isSuggested: isSuggestedValue(field.value, fieldState) }}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 4 }}>
        <Controller
          name="contactState"
          control={control}
          render={({ field, fieldState }) => (
            <FormTextField
              {...field}
              label="Contact State"
              fullWidth
              suggestionProps={{ isSuggested: isSuggestedValue(field.value, fieldState) }}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 4 }}>
        <Controller
          name="contactZipCode"
          control={control}
          render={({ field, fieldState }) => (
            <FormTextField
              {...field}
              label="Contact Zip Code"
              fullWidth
              suggestionProps={{ isSuggested: isSuggestedValue(field.value, fieldState) }}
            />
          )}
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Typography
          variant="subtitle2"
          color="text.primary"
          sx={{ mb: theme.spacing(3), mt: theme.spacing(2) }}
        >
          Regulatory Information
        </Typography>
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Controller
          name="regulatoryProgram"
          control={control}
          render={({ field, fieldState }) => (
            <FormTextField
              {...field}
              label="Regulatory Program"
              fullWidth
              suggestionProps={{ isSuggested: isSuggestedValue(field.value, fieldState) }}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Controller
          name="regulatoryCitations"
          control={control}
          render={({ field, fieldState }) => (
            <FormTextField
              {...field}
              label="Regulatory Citations"
              fullWidth
              suggestionProps={{ isSuggested: isSuggestedValue(field.value, fieldState) }}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Controller
          name="regulatoryAuthority"
          control={control}
          render={({ field, fieldState }) => (
            <FormTextField
              {...field}
              label="Regulatory Authority"
              fullWidth
              suggestionProps={{ isSuggested: isSuggestedValue(field.value, fieldState) }}
            />
          )}
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Typography
          variant="subtitle2"
          color="text.primary"
          sx={{ mb: theme.spacing(3), mt: theme.spacing(2) }}
        >
          Important Dates
        </Typography>
      </Grid>
      <Grid size={{ xs: 6 }}>
        <Controller
          name="issueDate"
          control={control}
          render={({ field, fieldState }) => (
            <DateField
              {...field}
              label="Issue Date"
              suggestionProps={{ isSuggested: isSuggestedValue(field.value, fieldState) }}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 6 }}>
        <Controller
          name="expirationDate"
          control={control}
          render={({ field, fieldState }) => (
            <DateField
              {...field}
              label="Expiration Date"
              suggestionProps={{ isSuggested: isSuggestedValue(field.value, fieldState) }}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 6 }}>
        <Controller
          name="effectiveDate"
          control={control}
          render={({ field, fieldState }) => (
            <DateField
              {...field}
              label="Effective Date"
              suggestionProps={{ isSuggested: isSuggestedValue(field.value, fieldState) }}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 6 }}>
        <Controller
          name="submissionDeadline"
          control={control}
          render={({ field, fieldState }) => (
            <DateField
              {...field}
              label="Submission Deadline"
              suggestionProps={{ isSuggested: isSuggestedValue(field.value, fieldState) }}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 6 }}>
        <Controller
          name="dueDate"
          control={control}
          render={({ field, fieldState }) => (
            <DateField
              {...field}
              label="Due Date"
              suggestionProps={{ isSuggested: isSuggestedValue(field.value, fieldState) }}
            />
          )}
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Typography
          variant="subtitle2"
          color="text.primary"
          sx={{ mb: theme.spacing(3), mt: theme.spacing(2) }}
        >
          Financial Information
        </Typography>
      </Grid>
      <Grid size={{ xs: 6 }}>
        <Controller
          name="amount"
          control={control}
          render={({ field, fieldState }) => (
            <FormTextField
              {...field}
              label="Amount"
              fullWidth
              textFieldProps={{ type: "number" }}
              suggestionProps={{ isSuggested: isSuggestedValue(field.value, fieldState) }}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 6 }}>
        <Controller
          name="currency"
          control={control}
          render={({ field, fieldState }) => (
            <FormTextField
              {...field}
              label="Currency"
              fullWidth
              suggestionProps={{ isSuggested: isSuggestedValue(field.value, fieldState) }}
            />
          )}
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Typography
          variant="subtitle2"
          color="text.primary"
          sx={{ mb: theme.spacing(3), mt: theme.spacing(2) }}
        >
          Related Documents
        </Typography>
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Controller
          name="supersedes"
          control={control}
          render={({ field, fieldState }) => (
            <FormTextField
              {...field}
              label="Supersedes"
              fullWidth
              suggestionProps={{ isSuggested: isSuggestedValue(field.value, fieldState) }}
            />
          )}
        />
      </Grid>
    </>
  );
}
